import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
} from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useStore } from "@tanstack/react-form";
import { useState } from "react";

import {
  ECR_PROCESS_LOCATIONS,
  ECR_STATUSES,
} from "api/memberPortal/ecr/ecrOptions.types";
import { FILE_VISIBILITY_TYPES } from "api/memberPortal/files/files.types";
import { useTriggerNotifyEcrCommentsReleasedMutation } from "api/memberPortal/notifications/ecr.api";
import { MaintenanceStub } from "api/memberPortal/project/maintenance/maintenance.types";
import {
  useGetProductQuery,
  useLazyGetProductQuery,
} from "api/memberPortal/project/product/product.api";
import {
  PRODUCT_STATUSES,
  ProductStatus,
} from "api/memberPortal/project/product/productStatuses.type";
import { useAppForm } from "components/Forms/AppForm";
import FileUploadSection from "components/Forms/components/sections/FileUploadSection/FileUploadSection";
import { useLoading } from "components/UI/LoadingSpinner/UseLoading";
import { useAuth } from "hooks/useAuth";

import {
  ECR_UPLOAD_TYPES,
  EcrFile,
  EcrForm,
  EcrFormInput,
  EcrFormSchema,
} from "./Ecr.schema";
import EcrCommentInfoSection from "./sections/EcrCommentInfoSection";
import EcrCommentTextSection from "./sections/EcrCommentTextSection";
import EcrProductSection from "./sections/EcrProductSection";
import EcrProtectionProfileSection from "./sections/EcrProtectionProfileSection";
import EcrTechnicalDecisionsSection from "./sections/EcrTechnicalDecisionsSection";
import { useEcrCascadingData } from "./useEcrCascadingData";
import { useEcrFormData } from "./useEcrFormData";
import { useEcrFormMutations } from "./useEcrFormMutations";

/** Maps product status to the appropriate ECR process location */
const getProcessLocation = (productStatus?: ProductStatus) => {
  if (
    productStatus &&
    (
      [
        PRODUCT_STATUSES.PRE_PROPOSAL,
        PRODUCT_STATUSES.PROPOSED,
        PRODUCT_STATUSES.PRE_EVALUATION,
      ] as ProductStatus[]
    ).includes(productStatus)
  ) {
    return ECR_PROCESS_LOCATIONS.CHECK_IN;
  }
  if (productStatus === PRODUCT_STATUSES.IN_ASSURANCE_MAINTENANCE) {
    return ECR_PROCESS_LOCATIONS.ASSURANCE_MAINTENANCE;
  }
  return ECR_PROCESS_LOCATIONS.CHECK_OUT;
};

interface EcrCommentFormProps {
  show: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  ecrid?: number;
  maintenance?: MaintenanceStub;
  projectId?: number;
  fetchEcrCounts?: () => void; //this is going to be removed once the table is updated, just leaving here so things work
}

const EcrCommentForm = ({
  show,
  onClose,
  onSuccess,
  ecrid,
  maintenance,
  projectId,
  fetchEcrCounts, //this is going to be removed once the table is updated, just leaving here so things work
}: EcrCommentFormProps) => {
  const [error, setError] = useState("");
  const isEditing = Boolean(ecrid);
  const { currentUser } = useAuth();

  // --- RTK Query hooks for edit mode ---
  const {
    ecr,
    existingDocs,
    existingTds,
    existingFiles,
    isLoading: isLoadingFormData,
  } = useEcrFormData(ecrid);

  // --- Fetch product (edit mode uses ECR's product, create mode uses preselected) ---
  const productId =
    projectId ?? maintenance?.product?.product_id ?? ecr?.pid?.product_id;
  const { data: product } = useGetProductQuery(productId ?? skipToken);
  const isProductLocked = Boolean(product) || isEditing;
  const vidNumber = product?.v_id;
  const [triggerGetProduct] = useLazyGetProductQuery();

  // --- Mutations ---
  const { handleCreate, handleUpdate } = useEcrFormMutations();
  const [triggerNotifyEcrCommentsReleased] =
    useTriggerNotifyEcrCommentsReleasedMutation();
  const { showLoadingSpinner, hideLoadingSpinner } = useLoading();

  // --- Default values (handles both create and edit) ---
  const ecrFormDefaultValues = {
    product: product
      ? {
          label: `VID ${product.v_id} - ${product.product_name}`,
          value: product.product_id,
        }
      : { label: "", value: projectId ?? ecr?.pid.product_id ?? "" },
    title: ecr?.gist ?? "",
    process_location:
      ecr?.process_location ?? getProcessLocation(product?.status),
    type: ecr?.type ?? "",
    status: ecr?.status ?? "",
    documentTypes: existingDocs?.map((doc) => doc.dtid.doctype) ?? [],
    cite: ecr?.cite ?? "",
    fileType: "",
    documentVisibility: "",
    fileLabel: "",
    files:
      (existingFiles?.map((file) => ({
        file_id: file.file_id,
        file: undefined,
        fileType: file.file_display_name ?? "",
        documentVisibility: file.private ? "Proprietary" : "Public",
        fileLabel: file.file_label ?? "",
        fileName: file.file_name ?? "",
        active: true,
      })) as EcrFile[]) ?? ([] as EcrFile[]),
    comment: ecr?.cmnt ?? "",
    rationale: ecr?.rationale ?? "",
    protectionProfile:
      ecr?.pp_id && ecr.pp_id.pp_id
        ? {
            label: ecr.pp_id.pp_short_name ?? "",
            value: ecr.pp_id.pp_id,
          }
        : { label: "", value: "" },
    technicalDecisions:
      existingTds?.map((td) => ({
        label: td.identifier ?? td.title ?? "",
        value: td.td_id,
      })) ?? [],
    requirement: ecr?.req ?? "",
  } as EcrFormInput;

  // --- Form setup ---
  const ecrForm = useAppForm({
    defaultValues: ecrFormDefaultValues,
    validators: {
      onSubmit: EcrFormSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        setError("");

        if (isEditing && ecr) {
          showLoadingSpinner("Updating ECR Comment...");
          await handleUpdate(
            value as EcrForm,
            ecr,
            existingDocs,
            existingTds,
            currentUser.id,
          );
        } else {
          showLoadingSpinner("Creating ECR Comment...");
          await handleCreate(
            value as EcrForm,
            currentUser.id,
            maintenance?.maintenance_id,
          );
        }

        hideLoadingSpinner();

        //we send an email when a comment is released for a vid
        if (value.status === ECR_STATUSES.RELEASED && projectId && vidNumber) {
          triggerNotifyEcrCommentsReleased({ product_id: projectId });
        }

        onSuccess?.();
        fetchEcrCounts?.(); //this is going to be removed once the table is updated, just leaving here so things work
        onClose();
      } catch (err) {
        hideLoadingSpinner();
        setError(
          isEditing
            ? "Error updating ECR Comment."
            : "Error creating ECR Comment.",
        );
        console.log("ECR submission error:", err);
      }
    },
    canSubmitWhenInvalid: true,
    onSubmitInvalid: async () => {
      setError("Please fill out all required fields before submitting");
    },
  });

  const cascadingProps = useStore(ecrForm.store, (state) => ({
    selectedProduct: state.values.product?.value,
    // TODO: do this typing better probably need to update the base props
    selectedPp: state.values.protectionProfile.value as number,
  }));

  // --- Cascading PP/TD options ---
  const {
    tdOptions,
    ppOptions,
    isLoading: isLoadingCascadingData,
  } = useEcrCascadingData(
    ecr,
    cascadingProps.selectedProduct || ecr?.pid.product_id,
    cascadingProps.selectedPp,
  );

  if (isLoadingFormData || isLoadingCascadingData) return null;

  return (
    <Dialog
      open={show}
      maxWidth="md"
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        hideLoadingSpinner();
        onClose();
      }}
      scroll="body"
    >
      <form onSubmit={(e) => e.preventDefault()}>
        <ecrForm.AppForm>
          <DialogTitle variant="h5">
            {isEditing
              ? `Edit ECR Comment ${ecr?.ecrid}`
              : maintenance
                ? `Add ECR Comment for AM ${maintenance.title}`
                : "Add ECR Comment"}

            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={(theme) => ({
                position: "absolute",
                right: 8,
                top: 8,
                color: theme.palette.grey[500],
              })}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <Divider
            variant="fullWidth"
            sx={{ opacity: ".25", borderColor: "black" }}
          />

          <DialogContent>
            <EcrProductSection
              form={ecrForm}
              isProductLocked={isProductLocked}
              product={product}
              // When a product is selected, fetch its details and
              // auto-set the process location based on product status
              onProductSelect={async (selectedProductId) => {
                // TODO: we can probably get rid of this call entirely, and do this prior to data submit.
                const selectedProduct =
                  await triggerGetProduct(selectedProductId).unwrap();
                ecrForm.setFieldValue(
                  "process_location",
                  getProcessLocation(selectedProduct.status),
                );
              }}
            />
            <EcrCommentInfoSection form={ecrForm} />
            <FileUploadSection
              form={ecrForm}
              fields={{
                fileType: "fileType",
                documentVisibility: "documentVisibility",
                fileLabel: "fileLabel",
                files: "files",
              }}
              uploadTypeOptions={Object.values(ECR_UPLOAD_TYPES)}
              visibilityOptions={Object.values(FILE_VISIBILITY_TYPES)}
            />
            <EcrCommentTextSection form={ecrForm} />
            <EcrProtectionProfileSection form={ecrForm} ppOptions={ppOptions} />
            <EcrTechnicalDecisionsSection
              form={ecrForm}
              tdOptions={tdOptions}
            />
          </DialogContent>

          <Paper elevation={1} sx={{ zIndex: 100 }}>
            <DialogActions sx={{ padding: 1.5 }}>
              {error && (
                <Alert sx={{ flex: 1 }} severity="error">
                  {error}
                </Alert>
              )}
              <Button
                variant="contained"
                color="error"
                sx={{ width: 150 }}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                sx={{
                  minWidth: 150,
                }}
                type="submit"
                onClick={ecrForm.handleSubmit}
              >
                {isEditing ? "Update" : "Add Comment"}
              </Button>
            </DialogActions>
          </Paper>
        </ecrForm.AppForm>
      </form>
    </Dialog>
  );
};
export default EcrCommentForm;
